import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { postRegisterPayment, updatePaymentDocument } from '../../../requests/api/contractPayment';
import useStyles from './styles';

const PAYMENT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' },
  { value: 'check', label: 'Cheque' },
  { value: 'monthly', label: 'Mensualidad' },
];

type DocumentModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  contractId?: string;
  type?: 'fee' | 'tuition';
  paymentDocumentId?: string;
  initialAmount?: number;
  initialComment?: string;
  initialPaymentMethod?: string;
  mode?: 'create' | 'edit';
  maxAmount?: number;
  paidAmount?: number;
};

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'application/pdf'];

function DocumentModal({
  open,
  onClose,
  onSuccess,
  contractId,
  type,
  paymentDocumentId,
  initialAmount = 0,
  initialComment = '',
  initialPaymentMethod = '',
  mode = 'create',
  maxAmount,
  paidAmount,
}: DocumentModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [amount, setAmount] = useState<number>(initialAmount);
  const [comment, setComment] = useState<string>(initialComment);
  const [paymentMethod, setPaymentMethod] = useState<string>(initialPaymentMethod);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [amountError, setAmountError] = useState<string>('');

  React.useEffect(() => {
    if (open) {
      setAmount(initialAmount);
      setComment(initialComment);
      setPaymentMethod(initialPaymentMethod);
      setAmountError('');
    }
  }, [open, initialAmount, initialComment, initialPaymentMethod]);

  const isSubmitDisabled = useMemo(() => {
    if (loading || !amount || Number.isNaN(Number(amount)) || Number(amount) <= 0) {
      return true;
    }
    if (maxAmount !== undefined && Number(amount) > maxAmount) {
      return true;
    }
    if (mode === 'create' && !file) {
      return true;
    }
    if (mode === 'edit') {
      // En modo edición, debe haber al menos un cambio
      const hasAmountChanged = amount !== initialAmount;
      const hasCommentChanged = comment !== initialComment;
      const hasPaymentMethodChanged = paymentMethod !== initialPaymentMethod;
      const hasNewFile = file !== null;

      if (!hasAmountChanged && !hasCommentChanged && !hasPaymentMethodChanged && !hasNewFile) {
        return true; // Deshabilitar si no hay cambios
      }
    }
    return false;
  }, [amount,
    comment,
    paymentMethod,
    file,
    loading,
    mode,
    initialAmount,
    initialComment,
    initialPaymentMethod,
    maxAmount,
  ]);

  const resetState = () => {
    setFile(null);
    setAmount(0);
    setComment('');
    setPaymentMethod('');
    setIsDragging(false);
    setAmountError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetState();
      onClose();
    }
  };

  const validateFile = (candidate: File) => {
    const sizeInMb = candidate.size / (1024 * 1024);
    if (sizeInMb > MAX_FILE_SIZE_MB) {
      enqueueSnackbar(`El archivo supera los ${MAX_FILE_SIZE_MB} MB permitidos.`, { variant: 'warning' });
      return false;
    }

    if (!ACCEPTED_TYPES.includes(candidate.type)) {
      enqueueSnackbar('Solo se permiten archivos PNG o JPEG.', { variant: 'warning' });
      return false;
    }

    return true;
  };

  const handleFileSelection = (candidate?: File | null) => {
    if (candidate && validateFile(candidate)) {
      setFile(candidate);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const candidate = event.target.files?.[0];
    handleFileSelection(candidate ?? null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const candidate = event.dataTransfer.files?.[0];
    handleFileSelection(candidate ?? null);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const truncateFileName = (name: string) => (
    name.length > 40 ? `${name.substring(0, 37)}...` : name
  );

  const handleSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }

    try {
      setLoading(true);

      if (mode === 'edit' && paymentDocumentId) {
        await updatePaymentDocument(
          paymentDocumentId,
          Number(amount),
          comment,
          file || undefined,
          paymentMethod || undefined,
        );
        enqueueSnackbar('Pago actualizado correctamente.', { variant: 'success' });
      } else if (mode === 'create' && contractId && type && file) {
        await postRegisterPayment({
          contractId,
          amount: Number(amount),
          comment,
          type,
          file,
          paymentMethod: paymentMethod || undefined,
        });
        enqueueSnackbar('Pago registrado correctamente.', { variant: 'success' });
      }

      onSuccess?.();
      resetState();
      onClose();
    } catch (error) {
      const errorMessage = mode === 'edit'
        ? 'No se pudo actualizar el pago. Intenta nuevamente.'
        : 'No se pudo registrar el pago. Intenta nuevamente.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className={classes.modalBackdrop}
    >
      <Card className={classes.card}>
        <CardContent className={classes.content}>
          <Box className={classes.header}>
            <Typography variant="h5" className={classes.title}>
              {mode === 'edit' ? 'Editar pago' : 'Registrar pago'}
            </Typography>
            <IconButton onClick={handleClose} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box>
            <Typography variant="body2" className={classes.description}>
              {mode === 'edit' ? 'Adjuntar nuevo comprobante de pago (opcional)' : 'Adjuntar comprobante de pago'}
            </Typography>

            {typeof maxAmount === 'number' && (
              <Box className={classes.amountSummary}>
                <Typography className={classes.owedAmountLabel}>
                  Monto por pagar:&nbsp;
                  <span className={classes.owedAmountValue}>
                    $
                    {maxAmount.toLocaleString()}
                  </span>
                </Typography>
                {typeof paidAmount === 'number' && (
                  <Typography className={classes.paidAmountLabel}>
                    Monto pagado:&nbsp;
                    <span className={classes.paidAmountValue}>
                      $
                      {paidAmount.toLocaleString()}
                    </span>
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Box
            className={`${classes.dropzone} ${isDragging ? classes.dropzoneActive : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <UploadFileIcon className={classes.dropzoneIcon} />
            <Typography variant="body1" className={classes.dropzoneText}>
              Haz clic para subir un archivo o arrástralo aquí
            </Typography>
            <Typography variant="body2" className={classes.dropzoneHelper}>
              Tamaño máximo de archivo: 10 MB
            </Typography>
            <Typography variant="body2" className={classes.dropzoneHelper}>
              Solo archivos PNG, JPEG o PDF
            </Typography>
            {file && (
              <Typography variant="body2" className={classes.fileName}>
                Archivo seleccionado: {truncateFileName(file.name)}
              </Typography>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              onChange={handleFileChange}
              className={classes.hiddenInput}
            />
          </Box>

          <Box className={classes.formControl}>
            <Box>
              <Typography className={classes.inputLabel}>Ingresar monto del pago</Typography>
              <TextField
                className={classes.inputField}
                fullWidth
                placeholder="Escribe el monto aquí"
                value={amount}
                error={!!amountError}
                helperText={amountError}
                onChange={(event) => {
                  const { value } = event.target;
                  if (/^\d*$/.test(value)) {
                    const numericValue = Number(value);
                    setAmount(numericValue);

                    if (maxAmount !== undefined && numericValue > maxAmount) {
                      setAmountError(`El monto no puede ser mayor al adeudado ($${maxAmount.toLocaleString()})`);
                    } else {
                      setAmountError('');
                    }
                  }
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Box>
            <Box>
              <Typography className={classes.inputLabel}>Medio de pago</Typography>
              <FormControl fullWidth className={`${classes.inputField} ${classes.paymentMethodSelect}`}>
                <InputLabel id="document-modal-payment-method-label">Seleccionar medio de pago</InputLabel>
                <Select
                  labelId="document-modal-payment-method-label"
                  value={paymentMethod}
                  label="Seleccionar medio de pago"
                  onChange={(event) => setPaymentMethod(event.target.value)}
                >
                  <MenuItem value="">
                    <em>Ninguno</em>
                  </MenuItem>
                  {PAYMENT_METHOD_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Typography className={classes.inputLabel}>Agregar comentario (opcional)</Typography>
              <TextField
                className={classes.inputField}
                fullWidth
                placeholder="Escribe tu comentario aquí"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                multiline
                minRows={1}
                maxRows={4}
              />
            </Box>
          </Box>

          <Box className={classes.actions}>
            <Button
              variant="contained"
              className={classes.cancelButton}
              onClick={handleClose}
            >
              <CloseIcon />
              Cancelar
            </Button>
            <Button
              variant="contained"
              className={classes.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              <UploadIcon />
              {(() => {
                if (loading) {
                  return mode === 'edit' ? 'Actualizando…' : 'Subiendo…';
                }
                return mode === 'edit' ? 'Actualizar' : 'Subir';
              })()}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
}

export default DocumentModal;
